import type {
  DisplayMinimumKey,
  DisplayMinimumView,
  InstrumentRef,
  InstrumentStatusView,
  MarketKind,
} from "@screener/protocol";
import { parseAmountField } from "@/lib/amount";

/**
 * Личные минимумы объёма §59 (design.md stage8-personal-display-filters,
 * Д5): чистая логика без React и сети. Минимум — фильтр показа клиента (§9),
 * ядро и демон строк по нему не отбирают.
 */

/**
 * Состояние списка личных минимумов — тот же снаряд, что у избранного. Живёт
 * здесь, а не в хуке: `derive.ts` и `situation-derive.ts` берут тип без
 * зависимости от React.
 */
export type DisplayMinimumsState =
  | { status: "loading" }
  | { status: "ready"; minimums: DisplayMinimumView[] }
  | { status: "unavailable" };

/** Биржа и рынок — единица, по которой задаётся минимум рынка. */
export interface MarketPair {
  exchange: string;
  market: MarketKind;
}

/**
 * Индекс минимумов по ключу `(биржа, рынок, база)`: значение — число для
 * сравнения со строкой. Строится один раз на список, а не поиском по массиву
 * на каждую строку.
 */
export type MinimumsIndex = ReadonlyMap<string, number>;

/** Ключ индекса. Массив в JSON отличает пустую базу от монеты с именем «null». */
function indexKey(exchange: string, market: MarketKind, base: string | null): string {
  return JSON.stringify([exchange, market, base]);
}

const indexCache = new WeakMap<readonly DisplayMinimumView[], MinimumsIndex>();

/**
 * Индекс кешируется по самому массиву: список в состоянии иммутабелен
 * (`applyMinimumPut` и `applyMinimumRemove` отдают новый), а `filterRows`
 * зовётся на каждую ситуацию — без кеша индекс пересобирался бы столько раз.
 */
export function buildMinimumsIndex(minimums: readonly DisplayMinimumView[]): MinimumsIndex {
  const cached = indexCache.get(minimums);
  if (cached !== undefined) {
    return cached;
  }
  const index = new Map<string, number>();
  for (const minimum of minimums) {
    index.set(indexKey(minimum.exchange, minimum.market, minimum.base), Number(minimum.min_usd));
  }
  indexCache.set(minimums, index);
  return index;
}

/**
 * Минимум, действующий для строки: запись монеты на её бирже и рынке, иначе
 * запись рынка, иначе `null` — фильтра по объёму у строки нет. Монета
 * перекрывает рынок и вверх, и вниз. База сравнивается точно, без смены
 * регистра: у бирж бывают базы не в верхнем регистре.
 */
export function effectiveMinimum(instrument: InstrumentRef, index: MinimumsIndex): number | null {
  const coin = index.get(indexKey(instrument.exchange, instrument.market, instrument.base));
  if (coin !== undefined) {
    return coin;
  }
  return index.get(indexKey(instrument.exchange, instrument.market, null)) ?? null;
}

/**
 * Список не прочитан по ошибке: личные минимумы строк не отбирают, и экран
 * говорит об этом словами. `loading` не в счёт — это краткий промежуток,
 * мигающая надпись была бы шумом.
 */
export function displayMinimumsUnavailable(state: DisplayMinimumsState): boolean {
  return state.status === "unavailable";
}

/**
 * Число действующих минимумов — рынков и монет вместе (stage8-card-minimum,
 * Д1). Запись ниже порога скринера не действует и не считается; запись без
 * порога в ответе считается: «ни да, ни нет» относится к пометке, а не к
 * счёту. Список не прочитан — `null`: число было бы выдумкой.
 */
export function activeMinimumsCount(state: DisplayMinimumsState): number | null {
  if (state.status !== "ready") {
    return null;
  }
  return state.minimums.filter(
    (minimum) => !isBelowDaemonFloor(minimum.min_usd, minimum.daemon_floor_usd),
  ).length;
}

/** Минимум монеты для шапки карточки: список прочитан (запись или её нет) либо не прочитан. */
export type CoinMinimumLookup =
  | { status: "read"; minimum: DisplayMinimumView | null }
  | { status: "unread" };

/**
 * Запись минимума монеты инструмента: биржа, рынок, база, котировка не важна
 * (§59 п. 1). Минимум рынка не возвращается — на карточке его нет (§62 п. 3).
 */
export function coinMinimumFor(
  state: DisplayMinimumsState,
  instrument: InstrumentRef,
): CoinMinimumLookup {
  if (state.status !== "ready") {
    return { status: "unread" };
  }
  return {
    status: "read",
    minimum:
      findMinimum(state.minimums, {
        exchange: instrument.exchange,
        market: instrument.market,
        base: instrument.base,
      }) ?? null,
  };
}

function sameKey(minimum: DisplayMinimumView, key: DisplayMinimumKey): boolean {
  return (
    minimum.exchange === key.exchange && minimum.market === key.market && minimum.base === key.base
  );
}

/** Запись по ключу или `undefined`. */
export function findMinimum(
  minimums: readonly DisplayMinimumView[],
  key: DisplayMinimumKey,
): DisplayMinimumView | undefined {
  return minimums.find((minimum) => sameKey(minimum, key));
}

/** Ответ `PUT` ложится в список: заменяет запись того же ключа или добавляется. */
export function applyMinimumPut(
  state: DisplayMinimumsState,
  saved: DisplayMinimumView,
): DisplayMinimumsState {
  if (state.status !== "ready") {
    return state;
  }
  const exists = state.minimums.some((minimum) => sameKey(minimum, saved));
  return {
    status: "ready",
    minimums: exists
      ? state.minimums.map((minimum) => (sameKey(minimum, saved) ? saved : minimum))
      : [...state.minimums, saved],
  };
}

/** Успешный `DELETE` убирает запись по ключу. */
export function applyMinimumRemove(
  state: DisplayMinimumsState,
  key: DisplayMinimumKey,
): DisplayMinimumsState {
  if (state.status !== "ready") {
    return state;
  }
  return {
    status: "ready",
    minimums: state.minimums.filter((minimum) => !sameKey(minimum, key)),
  };
}

/**
 * Число из поля окна: в долларах, пробелы и разряды допустимы (тот же
 * разбор `parseAmountField`, что у остальных полей сумм). Серверу уходит
 * десятичная строка без пробелов. Пустое, не число и ≤ 0 — ошибка поля:
 * пустота как «нет минимума» решается вызывающим до разбора.
 */
export function parseMinimumInput(raw: string): { ok: true; value: string } | { ok: false } {
  const parsed = parseAmountField(raw);
  if (!parsed.ok || parsed.value === null || !(Number(parsed.value) > 0)) {
    return { ok: false };
  }
  return { ok: true, value: parsed.value };
}

/**
 * Минимум ниже порога демона на своём рынке — не действует (§59 п. 2).
 * Порога нет — ни «действует», ни «не действует».
 */
export function isBelowDaemonFloor(minUsd: string, floorUsd: string | null): boolean {
  return floorUsd !== null && Number(minUsd) < Number(floorUsd);
}

/**
 * Порог демона для ключа до сохранения (§74 п. 7): у записи этого же ключа
 * (база `null` — минимум рынка), а без неё — у первой записи той же биржи и
 * рынка с непустым порогом. Пол поиска задан на рынок и от монеты не зависит
 * (§60 п. 1), поэтому порог соседа того же рынка — то же число. Пустой порог
 * своей записи — ответ сервера (монета в чёрном списке, рынок без пола), и
 * соседом он не подменяется. Запись другой биржи или рынка не используется.
 * Порог не считается на клиенте: берётся из ответов сервера.
 */
export function floorForInput(
  minimums: readonly DisplayMinimumView[],
  pair: MarketPair,
  base: string | null,
): string | null {
  const own = findMinimum(minimums, { ...pair, base });
  if (own !== undefined) {
    return own.daemon_floor_usd;
  }
  const sibling = minimums.find(
    (minimum) =>
      minimum.exchange === pair.exchange &&
      minimum.market === pair.market &&
      minimum.daemon_floor_usd !== null,
  );
  return sibling?.daemon_floor_usd ?? null;
}

function comparePairs(left: MarketPair, right: MarketPair): number {
  return left.exchange === right.exchange
    ? left.market.localeCompare(right.market)
    : left.exchange.localeCompare(right.exchange);
}

/**
 * Пары «биржа × рынок», о которых окно может говорить: те, что есть в
 * потоке, плюс пары уже заданных записей (биржа могла выйти из потока, а
 * запись остаётся и правится). Без повторов, отсортированы.
 */
export function marketPairs(
  instruments: readonly InstrumentStatusView[],
  minimums: readonly DisplayMinimumView[],
): MarketPair[] {
  const pairs = new Map<string, MarketPair>();
  const add = (exchange: string, market: MarketKind) => {
    pairs.set(JSON.stringify([exchange, market]), { exchange, market });
  };
  for (const entry of instruments) {
    add(entry.instrument.exchange, entry.instrument.market);
  }
  for (const minimum of minimums) {
    add(minimum.exchange, minimum.market);
  }
  return [...pairs.values()].sort(comparePairs);
}

/** Базы, которые поток несёт на паре, — подсказки поля монеты. */
export function baseHints(
  instruments: readonly InstrumentStatusView[],
  pair: MarketPair,
): string[] {
  const bases = new Set<string>();
  for (const entry of instruments) {
    if (entry.instrument.exchange === pair.exchange && entry.instrument.market === pair.market) {
      bases.add(entry.instrument.base);
    }
  }
  return [...bases].sort();
}

/**
 * База из поля: после `trim`; совпавшая с подсказкой без учёта регистра
 * берёт написание подсказки, прочая сохраняется как введена — угадывать
 * написание бирж клиенту нечем.
 */
export function resolveBase(input: string, hints: readonly string[]): string {
  const trimmed = input.trim();
  const lower = trimmed.toLowerCase();
  return hints.find((hint) => hint.toLowerCase() === lower) ?? trimmed;
}
